import * as React from 'react';
import { createContext, ReactNode, useContext, useMemo } from 'react';
import { useAutomationRules } from './hooks/useAutomationRules';
import { useCatalog } from './hooks/useCatalog';
import { useNewAutomationRule } from './hooks/useNewAutomationRule';
import { useNewPlaybook } from './hooks/useNewPlaybook';
import { usePlaybooks } from './hooks/usePlaybooks';
import { useTemplates } from './hooks/useTemplates';

type CatalogHook = ReturnType<typeof useCatalog>;
type PlaybooksHook = ReturnType<typeof usePlaybooks>;
type AutomationRulesHook = ReturnType<typeof useAutomationRules>;
type NewAutomationRuleHook = ReturnType<typeof useNewAutomationRule>;
type NewPlaybookHook = ReturnType<typeof useNewPlaybook>;
type TemplatesHook = ReturnType<typeof useTemplates>;

interface SoarStoreContextState {
  catalog: CatalogHook;
  playbooks: PlaybooksHook;
  automationRules: AutomationRulesHook;
  newAutomationRule: NewAutomationRuleHook;
  newPlaybook: NewPlaybookHook;
  templates: TemplatesHook;
}

const SoarStoreContext = createContext<SoarStoreContextState | undefined>(undefined);

export const SoarStoreProvider = ({ children }: { children: ReactNode }) => {
  const catalog = useCatalog();
  const playbooks = usePlaybooks();
  const automationRules = useAutomationRules(catalog.getEntityName, catalog.getEventName);
  const newAutomationRule = useNewAutomationRule();
  const newPlaybook = useNewPlaybook(undefined, catalog.actions, playbooks.playbooks);
  const templates = useTemplates();

  const value = useMemo(
    () => ({
      catalog,
      playbooks,
      automationRules,
      newAutomationRule,
      newPlaybook,
      templates,
    }),
    [catalog, playbooks, automationRules, newAutomationRule, newPlaybook, templates]
  );

  return <SoarStoreContext.Provider value={value}>{children}</SoarStoreContext.Provider>;
};

export const useSoarStore = () => {
  const context = useContext(SoarStoreContext);
  if (context === undefined) {
    throw new Error('useSoarStore must be used within a SoarStoreProvider');
  }
  return context;
};
